/**
 * Despia native iOS shell detection + helpers.
 *
 * Despia wraps the published web app in a WKWebView. We detect this so we can:
 *  - skip service worker registration (avoids stale shells & OAuth callback breakage)
 *  - hide Google sign-in (Google blocks OAuth in embedded webviews)
 *  - open external links in Safari via the `despia://openexternalbrowser=` scheme
 *
 * For all non-Despia visitors (regular web browsers, installed PWA), these helpers
 * fall back to standard web behavior, no behavioral change for current users.
 */

export const isDespiaApp = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Despia injects "despia" into the user agent string of its WKWebView.
  // Also accept an explicit window flag in case the UA is customised in the future.
  return /despia/i.test(ua) || (window as unknown as { __DESPIA__?: boolean }).__DESPIA__ === true;
};

/**
 * Open a URL the right way for the current environment.
 * - Inside Despia: hands the URL to iOS Safari via the despia:// scheme so
 *   external sites (Zoom, Calendly, Google Docs, etc.) open in a real browser.
 * - Everywhere else: standard window.open with noopener (current behavior).
 */
export const openExternal = (url: string): void => {
  if (!url) return;
  if (isDespiaApp()) {
    // Despia external-browser scheme. The shell intercepts this URL and opens
    // the target in the system browser instead of inside the webview.
    window.location.href = `despia://openexternalbrowser=${url}`;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};
